import { useState, useRef, useEffect } from "react";
import {
	createUserWithEmailAndPassword,
	sendEmailVerification,
} from "firebase/auth";
import { auth, db } from "../../firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/i18n";
import AuthForm from "./AuthForm";

const RegisterForm = () => {
	const { translate } = useTranslation();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const emailRef = useRef(null);

	// Automatically focus when the modal is ready AND visible
	useEffect(() => {
		// Small delay to ensure the animation is complete
		const timer = setTimeout(() => {
			emailRef.current?.focus();
		}, 400);
		return () => clearTimeout(timer);
	}, []);

	const handleRegister = async e => {
		e.preventDefault();
		setError(null);

		// User must accept the terms of use before proceeding
		if (!acceptedTerms) {
			setError(translate("register.error.mustAcceptTerms"));
			return;
		}

		setLoading(true);
		const emailNormalized = email.trim().toLowerCase();

		try {
			// Check if the email is whitelisted in Firestore
			const emailRef = doc(db, "whitelistedEmails", emailNormalized);
			const emailDoc = await getDoc(emailRef);

			if (!emailDoc.exists()) {
				setError(translate("register.error.emailNotWhitelisted"));
				setLoading(false);
				return;
			}

			// Create the user in Firebase Auth
			const userCredential = await createUserWithEmailAndPassword(
				auth,
				emailNormalized,
				password
			);
			const user = userCredential.user;

			// Send verification email
			await sendEmailVerification(user);

			// Create user document in Firestore with consent info
			await setDoc(doc(db, "users", user.uid), {
				email: emailNormalized,
				role: "user",
				createdAt: new Date().toISOString(),
				acceptedTerms: true,
				acceptedTermsAt: new Date().toISOString(),
				preferences: {
					useSystemTheme: true,
					groupMode: "category",
				},
			});

			// Redirect to login after email is sent
			navigate("/login");
		} catch (err) {
			console.error("Registration error:", err);
			if (err.code === "auth/email-already-in-use") {
				setError(translate("register.error.emailInUse"));
			} else if (err.code === "auth/invalid-email") {
				setError(translate("register.error.invalidEmail"));
			} else if (err.code === "auth/weak-password") {
				setError(translate("register.error.weakPassword"));
			} else {
				setError(translate("register.error.generic"));
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<form
			onSubmit={handleRegister}
			className="card glass-effect auth-card design-form"
		>
			<AuthForm
				email={email}
				setEmail={setEmail}
				password={password}
				setPassword={setPassword}
				error={error}
				translate={translate}
				emailRef={emailRef}
			/>

			<div className="form-group terms-checkbox">
				<label>
					<input
						type="checkbox"
						name="terms-checkbox"
						checked={acceptedTerms}
						onChange={() => setAcceptedTerms(prev => !prev)}
					/>
					<span>
						{translate("register.accept")}{" "}
						<a
							href="/terms"
							target="_blank"
							rel="noopener noreferrer"
							className="link-button"
						>
							{translate("register.terms")}
						</a>
					</span>
				</label>
			</div>

			<button type="submit" className="primary-button" disabled={loading}>
				{loading ? translate("register.loading") : translate("register.submit")}
			</button>
		</form>
	);
};

export default RegisterForm;
